import React, { useState, useEffect, useCallback } from 'react';
import { Box, VStack, HStack, Button, Text, Input, useToast, Heading, Image } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaUser, FaQrcode, FaCheck, FaLock } from 'react-icons/fa';
import QRCodeBox from './QRCodeBox';
import { supabase } from '../services/supabaseClient';
import { getAtendimentosAtivos } from '../api/getAtendimentosAtivos';
import LogoBarber from '../assets/Logo-Barber.png';

const MotionBox = motion(Box);

const CheckInScreen = ({ onCheckIn, onBack, onResumeSession, onAdminLogin }) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [isQrMode, setIsQrMode] = useState(true); // Começa com QR Code ativo
  const [atendimentosAtivos, setAtendimentosAtivos] = useState([]);
  const [showManualButton, setShowManualButton] = useState(false);
  const toast = useToast();

  // Timer para mostrar o botão de entrada manual após 10 segundos
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowManualButton(true);
    }, 10000);
    return () => clearTimeout(timer);
  }, []);

  // Atualiza as sessões ativas
  const fetchAtendimentos = useCallback(async () => {
    const atendimentos = await getAtendimentosAtivos();
    setAtendimentosAtivos(atendimentos);
  }, []);

  // Busca atendimentos ao montar e configura listener para check-ins mobile
  useEffect(() => {
    fetchAtendimentos();

    // Configura canal para receber notificações de check-in mobile
    const channel = supabase
      .channel('mobile-checkin')
      .on('broadcast', { event: 'mobile-checkin' }, async (payload) => {
        console.log('[CheckInScreen] Novo check-in mobile recebido:', payload);
        
        // Chama o onCheckIn com os dados do usuário mobile e o ID do atendimento
        onCheckIn({
          name: payload.payload.nome,
          phone: payload.payload.telefone,
          atendimentoId: payload.payload.atendimentoId
        });
      })
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [fetchAtendimentos, onCheckIn]);

  const handleSubmit = () => {
    if (!name.trim()) {
      toast({
        title: 'Nome obrigatório',
        description: 'Por favor, informe seu nome para continuar.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    if (!phone.trim()) {
      toast({
        title: 'Telefone obrigatório',
        description: 'Por favor, informe seu telefone para continuar.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Validar formato do telefone (pelo menos 10 dígitos)
    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      toast({
        title: 'Telefone inválido',
        description: 'Por favor, informe um número de telefone válido com DDD.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    onCheckIn({ name, phone: phoneDigits });
  };

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      height="100vh"
      width="100%"
      bg="linear-gradient(180deg, rgba(0,0,0,0.95) 0%, rgba(26,32,44,0.95) 100%)"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      padding={8}
      position="relative"
      overflow="hidden"
    >
      {/* Efeito de brilho no fundo */}
      <Box
        position="absolute"
        top="-50%"
        left="-50%"
        width="200%"
        height="200%"
        background="radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)"
        transform="rotate(-45deg)"
        pointerEvents="none"
      />

      {/* Conteúdo principal */}
      <Box 
        width="100%" 
        maxWidth="600px" 
        display="flex" 
        flexDirection="column" 
        alignItems="center"
      >
        {/* Logo */}
        <Box
          width="220px"
          height="220px"
          mb={8}
          position="relative"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Box
            position="absolute"
            width="100%"
            height="100%"
            borderRadius="full"
            bg="linear-gradient(45deg, #FFD700, #FFA500)"
            opacity={0.2}
            filter="blur(20px)"
          />
          <Image
            src={LogoBarber}
            alt="Barbearia Freire Logo"
            width="180px"
            height="180px"
            objectFit="contain"
          />
        </Box>

        {/* Título */}
        <Heading
          fontSize="3xl"
          fontWeight="bold"
          textAlign="center"
          color="white"
          mb={6}
        >
          {isQrMode ? 'Escaneie o QR Code para check-in' : 'Bem-vindo à Barbearia Freire'}
        </Heading>

        {/* Área do QR Code ou Formulário */}
        {isQrMode ? (
          <Box
            position="relative"
            width="280px"
            height="280px"
            borderRadius="md"
            bg="white"
            p={0}
            mb={6}
            border="2px solid"
            borderColor="gray.800"
          >
            <QRCodeBox />
          </Box>
        ) : (
          <VStack spacing={4} width="100%" maxW="400px" mb={6}>
            <Input
              placeholder="Digite seu nome"
              size="lg"
              value={name}
              onChange={(e) => setName(e.target.value)}
              bg="whiteAlpha.100"
              border="1px solid"
              borderColor="yellow.500"
              color="white"
              _placeholder={{ color: 'whiteAlpha.500' }}
              _focus={{ borderColor: 'yellow.300', boxShadow: '0 0 0 1px rgba(255, 215, 0, 0.6)' }}
            />
            <Input
              placeholder="Digite seu telefone com DDD"
              size="lg"
              value={phone}
              onChange={(e) => {
                // Permitir apenas números, parênteses, hífen e espaços
                const value = e.target.value.replace(/[^0-9()\-\s]/g, '');
                setPhone(value);
              }}
              bg="whiteAlpha.100"
              border="1px solid"
              borderColor="yellow.500"
              color="white"
              _placeholder={{ color: 'whiteAlpha.500' }}
              _focus={{ borderColor: 'yellow.300', boxShadow: '0 0 0 1px rgba(255, 215, 0, 0.6)' }}
              isRequired
            />
          </VStack>
        )}

        {/* Botões de ação */}
        <HStack spacing={4} width="100%" justifyContent="center" mt={6}>
          {/* Botão de voltar sempre visível */}
          <Button
            onClick={onBack}
            variant="ghost"
            colorScheme="gray"
            size="lg"
            _hover={{ bg: 'whiteAlpha.200' }}
          >
            Voltar
          </Button>
          
          {isQrMode && showManualButton ? (
            <Button
              leftIcon={<FaUser />}
              onClick={() => setIsQrMode(false)}
              colorScheme="yellow"
              variant="outline"
              size="lg"
              _hover={{ bg: 'whiteAlpha.200' }}
            >
              Entrada Manual
            </Button>
          ) : !isQrMode ? (
            <Button
              leftIcon={<FaQrcode />}
              onClick={() => setIsQrMode(true)}
              colorScheme="yellow"
              variant="outline"
              size="lg"
              _hover={{ bg: 'whiteAlpha.200' }}
            >
              Usar QR Code
            </Button>
          ) : null}
          
          {!isQrMode && (
            <Button
              leftIcon={<FaCheck />}
              colorScheme="yellow"
              size="lg"
              onClick={handleSubmit}
              isDisabled={!name.trim() || !phone.trim()}
            >
              Continuar
            </Button>
          )}
        </HStack>

        {/* Lista de Atendimentos Ativos */}
        {atendimentosAtivos.length > 0 && (
          <Box
            width="100%"
            maxWidth="600px"
            mt={10}
          >
            <Heading size="md" color="yellow.400" mb={4} textAlign="center">
              Sessões Ativas
            </Heading>
            <VStack spacing={3} width="100%">
              {atendimentosAtivos.map((atendimento) => (
                <Button
                  key={atendimento.id}
                  width="100%"
                  onClick={() => onResumeSession(atendimento)}
                  colorScheme="yellow"
                  variant="solid"
                  size="lg"
                  mb={2}
                >
                  {atendimento.nome_cliente}
                </Button>
              ))}
            </VStack>
          </Box>
        )}
        
        {/* Botão de Acesso Administrativo */}
        <Box width="100%" maxWidth="600px" mt={6} display="flex" justifyContent="center">
          <Button
            leftIcon={<FaLock />}
            onClick={onAdminLogin}
            variant="outline"
            colorScheme="gray"
            size="md"
            opacity={0.7}
            _hover={{ opacity: 1 }}
          >
            Como Administrador
          </Button>
        </Box>
      </Box>
    </MotionBox>
  );
};

export default CheckInScreen;
